"use client";

import React from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import { ChevronLeft, ChevronRight } from "lucide-react";

import {
  productSliderStyles,
  responsiveCss,
} from "./ProductSlider.styles";
import {
  ProductSliderBaseProps,
  ProductSliderCard,
} from "./ProductSlider.shared";
import { applyProductCollectionFilter } from "./productCollection.shared";
import ProductEmptyState from "../ProductEmptyState/ProductEmptyState";

import "swiper/css";
import "swiper/css/navigation";

export type ProductSliderProps = ProductSliderBaseProps & {
  slidesPerViewMobile?: number;
  slidesPerViewTablet?: number;
  slidesPerViewDesktop?: number;
  spaceBetween?: number;
};

export default function ProductSlider({
  items,
  Link,
  title = "Featured Products",
  eyebrow = "Products",
  description,
  currency = "AUD",
  ctaText = "View Product",
  slidesPerViewMobile = 1.15,
  slidesPerViewTablet = 2.1,
  slidesPerViewDesktop = 3.2,
  spaceBetween = 18,
  filter,
  emptyTitle,
  emptyDescription,
}: ProductSliderProps) {
  const filteredItems = applyProductCollectionFilter(items, filter);
  const hasMultipleItems = filteredItems.length > 1;

  return (
    <section style={productSliderStyles.section}>
      <style>{responsiveCss}</style>

      <div style={productSliderStyles.headerWrap}>
        {eyebrow ? <p style={productSliderStyles.eyebrow}>{eyebrow}</p> : null}
        {title ? (
          <h2 className="product-slider-title" style={productSliderStyles.title}>
            {title}
          </h2>
        ) : null}
        {description ? <p style={productSliderStyles.description}>{description}</p> : null}
      </div>

      {filteredItems.length === 0 ? (
        <ProductEmptyState title={emptyTitle} description={emptyDescription} />
      ) : (
      <div style={{ position: "relative" }}>
        <Swiper
          modules={[Navigation]}
          navigation={
            hasMultipleItems
              ? {
                  prevEl: ".product-slider-prev",
                  nextEl: ".product-slider-next",
                }
              : false
          }
          spaceBetween={spaceBetween}
          slidesPerView={slidesPerViewMobile}
          breakpoints={{
            768: { slidesPerView: slidesPerViewTablet, spaceBetween },
            1024: { slidesPerView: slidesPerViewDesktop, spaceBetween },
          }}
          className="product-slider-swiper"
        >
          {filteredItems.map((item) => (
            <SwiperSlide key={item.id}>
              <ProductSliderCard
                item={item}
                Link={Link}
                currency={currency}
                ctaText={ctaText}
              />
            </SwiperSlide>
          ))}
        </Swiper>

        {hasMultipleItems ? (
          <>
            <button
              type="button"
              className="product-slider-prev"
              aria-label="Previous products"
              style={{
                ...productSliderStyles.iconButton,
                position: "absolute",
                left: -10,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 5,
              }}
            >
              <ChevronLeft size={18} strokeWidth={2.25} />
            </button>
            <button
              type="button"
              className="product-slider-next"
              aria-label="Next products"
              style={{
                ...productSliderStyles.iconButton,
                position: "absolute",
                right: -10,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 5,
              }}
            >
              <ChevronRight size={18} strokeWidth={2.25} />
            </button>
          </>
        ) : null}
      </div>
      )}
    </section>
  );
}
